import React, { useMemo, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  EmptyState,
  Progress,
  ResponseErrorPanel,
  Select,
  SelectItem,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { Card, CardActionArea, CardContent, Chip, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';

const PROJECT_ANNOTATION = 'dev.azure.com/project';
const AREA_PATH_ANNOTATION = 'dev.azure.com/area-path';
const TAG_ANNOTATION = 'dev.azure.com/tag';

// Azure DevOps' workitems batch-get endpoint rejects requests for more than
// 200 ids at once, so id lists longer than this must be split into chunks
// and fetched separately.
const WORK_ITEM_BATCH_SIZE = 200;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export const isAzureWorkItemsAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[PROJECT_ANNOTATION]);

type WorkItem = {
  id: number;
  fields: {
    'System.Title': string;
    'System.State': string;
    'System.WorkItemType': string;
    'System.IterationPath'?: string;
    'System.AssignedTo'?: { displayName: string; uniqueName?: string };
  };
  _links?: { html?: { href: string } };
};

const ALL_FILTER = '__all__';
const MINE_FILTER = '__mine__';
const CURRENT_SPRINT_FILTER = '__current_sprint__';

type IterationNode = {
  path: string;
  hasChildren?: boolean;
  children?: IterationNode[];
  attributes?: { startDate?: string; finishDate?: string };
};

// Azure DevOps classification-node paths always embed a literal "Iteration"
// segment right after the project name (e.g. \Project\Iteration\Sprint 1)
// that never appears in System.IterationPath (e.g. Project\Sprint 1) - drop it.
export const toIterationPath = (nodePath: string): string => {
  const segments = nodePath.split('\\').filter(Boolean);
  return [segments[0], ...segments.slice(2)].join('\\');
};

export const flattenIterations = (
  node: IterationNode,
): { iterationPath: string; startDate: Date; finishDate: Date }[] => {
  const own =
    node.attributes?.startDate && node.attributes?.finishDate
      ? [
          {
            iterationPath: toIterationPath(node.path),
            startDate: new Date(node.attributes.startDate),
            finishDate: new Date(node.attributes.finishDate),
          },
        ]
      : [];
  return [...own, ...(node.children ?? []).flatMap(flattenIterations)];
};

export const stateColor = (state: string): 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error' => {
  const lower = state.toLowerCase();
  if (
    lower.includes('done') ||
    lower.includes('closed') ||
    lower.includes('resolved') ||
    lower.includes('passed') ||
    lower.includes('accepted') ||
    lower.includes('approved')
  ) {
    return 'success';
  }
  if (lower.includes('failed') || lower.includes('rejected') || lower.includes('blocked')) {
    return 'error';
  }
  if (lower.includes('progress') || lower.includes('active') || lower.includes('review')) {
    return 'primary';
  }
  if (
    lower.includes('ready') ||
    lower.includes('qa') ||
    lower.includes('test') ||
    lower.includes('merge')
  ) {
    return 'info';
  }
  if (lower.includes('to do') || lower.includes('new') || lower.includes('backlog')) {
    return 'default';
  }
  return 'warning';
};

export const typeColor = (type: string): string => {
  const lower = type.toLowerCase();
  if (lower.includes('bug')) return '#d32f2f';
  if (lower.includes('task')) return '#f9a825';
  if (lower.includes('feature')) return '#7b1fa2';
  if (lower.includes('epic')) return '#4a148c';
  return '#1976d2';
};

type OpenPullRequest = { title: string; body: string; url: string };

export const findOpenPr = (
  workItemId: number,
  openPullRequests: OpenPullRequest[] | undefined,
): OpenPullRequest | undefined => {
  const pattern = new RegExp(`AB#\\s*${workItemId}(?!\\d)`, 'i');
  return openPullRequests?.find(
    pr => pattern.test(pr.title) || pattern.test(pr.body),
  );
};

const AzureWorkItemsCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const reposAnnotation = entity.metadata.annotations?.['github-pr-dashboard/repos'];

  const { value: openPullRequests } = useAsync(async (): Promise<
    OpenPullRequest[]
  > => {
    if (!reposAnnotation) {
      return [];
    }
    const baseUrl = await discoveryApi.getBaseUrl('github-pr-dashboard');
    const response = await fetchApi.fetch(
      `${baseUrl}/prs?repos=${encodeURIComponent(reposAnnotation)}`,
    );
    if (!response.ok) {
      return [];
    }
    const results: { pullRequests: OpenPullRequest[] }[] = await response.json();
    return results.flatMap(result => result.pullRequests);
  }, [reposAnnotation]);

  const { value: currentUserEmail } = useAsync(async () => {
    const profile = await identityApi.getProfileInfo();
    return profile.email?.toLowerCase();
  }, [identityApi]);

  const project = entity.metadata.annotations?.[PROJECT_ANNOTATION] ?? '';
  const areaPath = entity.metadata.annotations?.[AREA_PATH_ANNOTATION];
  const tag = entity.metadata.annotations?.[TAG_ANNOTATION];
  const [assigneeFilter, setAssigneeFilter] = useState(ALL_FILTER);
  const [iterationFilter, setIterationFilter] = useState(ALL_FILTER);
  const [searchTerm, setSearchTerm] = useState('');

  const { value: workItems, loading, error } = useAsync(async (): Promise<WorkItem[]> => {
    const proxyUrl = await discoveryApi.getBaseUrl('proxy');
    const baseUrl = `${proxyUrl}/azure-devops/api/${encodeURIComponent(project)}/_apis/wit`;

    const wiqlQuery = [
      `SELECT [System.Id] FROM WorkItems`,
      `WHERE [System.TeamProject] = '${project}'`,
      `AND [System.State] NOT IN ('Closed', 'Removed', 'Done')`,
      areaPath ? `AND [System.AreaPath] UNDER '${areaPath}'` : '',
      tag ? `AND [System.Tags] CONTAINS '${tag}'` : '',
      `ORDER BY [System.ChangedDate] DESC`,
    ]
      .filter(Boolean)
      .join(' ');

    const wiqlResponse = await fetchApi.fetch(`${baseUrl}/wiql?api-version=7.1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: wiqlQuery }),
    });
    if (!wiqlResponse.ok) {
      throw new Error(
        `Failed to query Azure DevOps work items: ${wiqlResponse.status} ${wiqlResponse.statusText}`,
      );
    }
    const wiqlBody = await wiqlResponse.json();
    const ids: number[] = (wiqlBody.workItems ?? []).map(
      (wi: { id: number }) => wi.id,
    );
    if (ids.length === 0) {
      return [];
    }

    const batches = await Promise.all(
      chunk(ids, WORK_ITEM_BATCH_SIZE).map(async batchIds => {
        const detailsResponse = await fetchApi.fetch(
          `${baseUrl}/workitems?ids=${batchIds.join(',')}&fields=System.Title,System.State,System.WorkItemType,System.IterationPath,System.AssignedTo&$expand=links&api-version=7.1`,
        );
        if (!detailsResponse.ok) {
          throw new Error(
            `Failed to fetch Azure DevOps work item details: ${detailsResponse.status} ${detailsResponse.statusText}`,
          );
        }
        const detailsBody = await detailsResponse.json();
        return detailsBody.value ?? [];
      }),
    );
    return batches.flat();
  }, [project, areaPath, tag]);

  const { value: currentSprintPath } = useAsync(async (): Promise<
    string | undefined
  > => {
    const proxyUrl = await discoveryApi.getBaseUrl('proxy');
    const response = await fetchApi.fetch(
      `${proxyUrl}/azure-devops/api/${encodeURIComponent(
        project,
      )}/_apis/wit/classificationnodes/iterations?$depth=10&api-version=7.1`,
    );
    if (!response.ok) {
      return undefined;
    }
    const tree = await response.json();
    const iterations = flattenIterations(tree);
    const now = new Date();
    return iterations.find(i => i.startDate <= now && now <= i.finishDate)
      ?.iterationPath;
  }, [project]);

  const assigneeItems: SelectItem[] = useMemo(
    () => [
      { label: 'All', value: ALL_FILTER },
      ...(currentUserEmail ? [{ label: 'Me', value: MINE_FILTER }] : []),
      ...Array.from(
        new Set(
          (workItems ?? [])
            .map(item => item.fields['System.AssignedTo']?.displayName)
            .filter((name): name is string => Boolean(name)),
        ),
      )
        .sort()
        .map(name => ({ label: name, value: name })),
    ],
    [workItems, currentUserEmail],
  );

  const iterationItems: SelectItem[] = useMemo(
    () => [
      { label: 'All', value: ALL_FILTER },
      ...(currentSprintPath
        ? [{ label: `Current Sprint (${currentSprintPath})`, value: CURRENT_SPRINT_FILTER }]
        : []),
      ...Array.from(
        new Set(
          (workItems ?? [])
            .map(item => item.fields['System.IterationPath'])
            .filter((path): path is string => Boolean(path)),
        ),
      )
        .sort()
        .map(path => ({ label: path, value: path })),
    ],
    [workItems, currentSprintPath],
  );

  const filteredWorkItems = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return (workItems ?? []).filter(item => {
      const assignedTo = item.fields['System.AssignedTo'];
      const assigneeMatches =
        assigneeFilter === ALL_FILTER ||
        (assigneeFilter === MINE_FILTER
          ? assignedTo?.uniqueName?.toLowerCase() === currentUserEmail
          : assignedTo?.displayName === assigneeFilter);
      const iterationMatches =
        iterationFilter === ALL_FILTER ||
        (iterationFilter === CURRENT_SPRINT_FILTER
          ? item.fields['System.IterationPath'] === currentSprintPath
          : item.fields['System.IterationPath'] === iterationFilter);
      const searchMatches =
        !search ||
        item.fields['System.Title'].toLowerCase().includes(search) ||
        String(item.id).includes(search);
      return assigneeMatches && iterationMatches && searchMatches;
    });
  }, [
    workItems,
    assigneeFilter,
    iterationFilter,
    currentUserEmail,
    currentSprintPath,
    searchTerm,
  ]);

  if (loading) {
    return (
      <InfoCard title="Work Items">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Work Items">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  if (!workItems || workItems.length === 0) {
    return (
      <InfoCard title="Work Items">
        <EmptyState
          title="No open work items"
          missing="data"
          description="There are no open work items for this component in Azure Boards."
        />
      </InfoCard>
    );
  }

  return (
    <InfoCard
      title="Work Items"
      action={
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search tickets..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      }
    >
      <Grid2 container spacing={2} sx={{ marginBottom: 1 }}>
        <Grid2 size={6}>
          <Select
            label="Assigned to"
            items={assigneeItems}
            selected={assigneeFilter}
            onChange={value => setAssigneeFilter(value as string)}
          />
        </Grid2>
        <Grid2 size={6}>
          <Select
            label="Sprint"
            items={iterationItems}
            selected={iterationFilter}
            onChange={value => setIterationFilter(value as string)}
          />
        </Grid2>
      </Grid2>
      {filteredWorkItems.length === 0 ? (
        <EmptyState
          title="No matching work items"
          missing="data"
          description="No open work items match the selected filters."
        />
      ) : (
      <Grid2 container direction="column" spacing={1}>
        {filteredWorkItems.map(item => {
          const openPr = findOpenPr(item.id, openPullRequests);
          return (
          <Grid2 key={item.id} size={12}>
            <Card
              sx={{
                width: '100%',
                borderLeft: `4px solid ${typeColor(item.fields['System.WorkItemType'])}`,
              }}
            >
              <CardActionArea
                href={item._links?.html?.href ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: 1.5 }}
              >
                <CardContent sx={{ flexGrow: 1, minWidth: 0, padding: '0 !important' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
                      #{item.id}
                    </Typography>{' '}
                    {item.fields['System.Title']}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: typeColor(item.fields['System.WorkItemType']), fontWeight: 600 }}
                    >
                      {item.fields['System.WorkItemType']}
                    </Typography>
                    {item.fields['System.AssignedTo']?.displayName
                      ? ` · Assigned to ${item.fields['System.AssignedTo'].displayName}`
                      : ''}
                  </Typography>
                </CardContent>
                {openPr && (
                  <Chip
                    label="PR Open"
                    color="success"
                    size="small"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(openPr.url, '_blank', 'noopener,noreferrer');
                    }}
                    sx={{ marginLeft: 2, flexShrink: 0, fontWeight: 600 }}
                  />
                )}
                <Chip
                  label={item.fields['System.State']}
                  color={stateColor(item.fields['System.State'])}
                  size="small"
                  sx={{ marginLeft: 1, flexShrink: 0, fontWeight: 600 }}
                />
              </CardActionArea>
            </Card>
          </Grid2>
          );
        })}
      </Grid2>
      )}
    </InfoCard>
  );
};

export default AzureWorkItemsCard;
